import { AuthHospitalModel } from './../../models/common/auth-hospital.model';
import { inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { UserModel } from '../../models/apis/system/user/user.model';
import { UserService } from '../apis/system/user.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthRoleModel } from '../../models/common/auth-role.model';
import { HospitalService } from '../apis/system/hospital.service';
import { CommonConstants } from '../../constants/common.constants';
import { GetHospitalsLitePaginatedModel } from '../../models/apis/system/hospital/get-hospitals-lite-paginated.model';
import { LoggerService } from './logger.service';
import { environment } from '../../../../environments/environment';
import { AuthDepartmentModel } from '../../models/common/auth-department.model';
import { PutUserLastTokenModel } from '../../models/apis/system/user/put-user-last-token.model';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly _userService = inject(UserService);
    private readonly _oidcSecurityService = inject(OidcSecurityService);
    private readonly _hospitalService = inject(HospitalService);
    private readonly _errorLoggerService = inject(LoggerService);
    private readonly _userLastSession = 'USER:LAST_TOKEN';

    private readonly _userAccountSubject: Subject<UserModel> =
        new Subject<UserModel>();
    userAccountObservable = this._userAccountSubject.asObservable();

    private readonly _userHospitalsSubject: Subject<Array<AuthHospitalModel>> =
        new Subject<Array<AuthHospitalModel>>();
    userHospitalsObservable = this._userHospitalsSubject.asObservable();

    private readonly _userRolesSubject: Subject<Array<AuthRoleModel>> =
        new Subject<Array<AuthRoleModel>>();
    userRolesObservable = this._userRolesSubject.asObservable();

    private readonly _userDepartmentsSubject: Subject<
        Array<AuthDepartmentModel>
    > = new Subject<Array<AuthDepartmentModel>>();
    userDepartmentsObservable = this._userDepartmentsSubject.asObservable();

    private readonly _userAclsSubject: Subject<Array<string>> = new Subject<
        Array<string>
    >();
    userAclsObservable = this._userAclsSubject.asObservable();

    private readonly _defaultHospitalSubject: Subject<AuthHospitalModel> =
        new Subject<AuthHospitalModel>();
    defaultHospitalObservable = this._defaultHospitalSubject.asObservable();

    private readonly _navbarBackgroundSubject: Subject<string> = new Subject<string>();
    navbarBackgroundObservable = this._navbarBackgroundSubject.asObservable();

    private readonly _navbarForegroundSubject: Subject<string> = new Subject<string>();
    navbarForegroundObservable = this._navbarForegroundSubject.asObservable();

    private _currentUser: UserModel | null = null;
    private _hospitals: Array<AuthHospitalModel> = [];
    private _roles: Array<AuthRoleModel> = [];
    private _departments: Array<AuthDepartmentModel> = [];
    private _acls: Array<string> = [];
    private _currentSelectedHospital: AuthHospitalModel | null = null;
    private _hospitalsFilterPageIndex = 0;
    private _hospitalsFilterPageSize = CommonConstants.defaultDropdownFetchSize;
    constructor() {}

    async initAuth() {
        try {
            let isAuthenticated = await this._oidcSecurityService
                .isAuthenticated()
                .toPromise();
            if (isAuthenticated) {
                var authData = await this._userService
                    .getAuthenticated()
                    .toPromise();
                if (authData?.isError !== true && authData?.data) {
                    this._currentUser = authData.data;
                    await this.setHospitals();
                    await this.setRoles();
                    await this.setAcls();
                    await this.setDepartments();

                    this._userAccountSubject.next(this._currentUser);
                }
            } else {
                this._oidcSecurityService.authorize();
            }
        } catch (err) {
            console.error('Error while setting up current user profile!', err);
        }
    }

    async getCurrentUser(): Promise<UserModel | null> {
        if (!this._currentUser) {
            let isAuthenticated = await this._oidcSecurityService
                .isAuthenticated()
                .toPromise();
            if (isAuthenticated) {
                await this.initAuth();
                return this._currentUser;
            } else {
                this._oidcSecurityService.authorize();
            }
        }
        return this._currentUser;
    }

    async isAdminUser(): Promise<boolean> {
        var user = await this.getCurrentUser();
        return user?.isAdminUser!;
    }

    async setHospitals() {
        var user = await this.getCurrentUser();

        this._hospitals = [];

        if (await this.isAdminUser()) {
            try {
                let request = {
                    pageIndex: this._hospitalsFilterPageIndex,
                    pageSize: this._hospitalsFilterPageSize,
                    isActive: true,
                } as GetHospitalsLitePaginatedModel;

                var response = await this._hospitalService
                    .getPaginated(request)
                    .toPromise();
                if (response?.isError != true && response?.data) {
                    for (let hospital of response.data.hospitals) {
                        this._hospitals.push({
                            key: hospital.id,
                            value: hospital.name,
                            code: hospital?.code,
                            navbarBackgroundColor: hospital?.navbarBackgroundColor,
                            navbarForegroundColor: hospital?.navbarForegroundColor
                        });
                    }
                } else {
                    this._errorLoggerService.logError(
                        'Get User Hospitals Error',
                        response?.errors!
                    );
                }
            } catch (err: any) {
                this._errorLoggerService.logError(
                    'Get User Hospitals Error',
                    err
                );
            }
        } else {
            if (user && user.hospitals && user.hospitals.length > 0) {
                user.hospitals.forEach((value) => {
                    this._hospitals.push({
                        key: value.id,
                        value: value.name,
                        code: value.code,
                        navbarBackgroundColor: value.navbarBackgroundColor,
                        navbarForegroundColor: value.navbarForegroundColor
                    });
                });
            }
        }

        if (user && user.userSetting && user.userSetting.defaultHospitalId) {
            const storedHospitalId = localStorage.getItem(
                CommonConstants.SelectedHospitalId
            );
            if (storedHospitalId) {
                this.setSelectedHospital(Number(storedHospitalId));
            } else if (user?.userSetting?.defaultHospitalId) {
                this.setSelectedHospital(user.userSetting.defaultHospitalId);
            }
        }
        this._userHospitalsSubject.next(this._hospitals);
    }

    setSelectedHospital(hospitalId: number) {
        let hospital = this._hospitals.find((c) => c.key == hospitalId);
        if (hospital) {
            this._currentSelectedHospital = hospital;
            localStorage.setItem(CommonConstants.navbarBackgroundColorKey, hospital.navbarBackgroundColor ?? CommonConstants.navbarBackgroundColorDefault);
            this._navbarBackgroundSubject.next(hospital.navbarBackgroundColor ?? CommonConstants.navbarBackgroundColorDefault);
            localStorage.setItem(CommonConstants.navbarForegroundColorKey, hospital.navbarForegroundColor ?? CommonConstants.navbarForegroundColorDefault);
            this._navbarForegroundSubject.next(hospital.navbarForegroundColor ?? CommonConstants.navbarForegroundColorDefault);
            this._defaultHospitalSubject.next(hospital);
        }
    }

    getCurrentSelectedHospital() {
        return this._currentSelectedHospital;
    }

    getHospitals() {
        return new Promise<Array<AuthHospitalModel>>((resolve, reject) =>
            resolve(this._hospitals ?? [])
        );
    }

    async setRoles() {
        var user = await this.getCurrentUser();
        this._roles = [];
        if (user && user.roles && user.roles.length > 0) {
            user.roles.forEach((value) => {
                this._roles.push({
                    key: value.id,
                    value: value.name,
                });
            });
        }
        this._userRolesSubject.next(this._roles);
    }

    getRoles(): Array<AuthRoleModel> {
        return this._roles ?? [];
    }

    async setDepartments() {
        var user = await this.getCurrentUser();
        this._departments = [];
        if (user && user.departments && user.departments.length > 0) {
            user.departments.forEach((value) => {
                this._departments.push({
                    key: value.id,
                    value: value.name,
                    code: value.code,
                    costCentreId: value.costCentreId
                });
            });
        }
        this._userDepartmentsSubject.next(this._departments);
    }

    getDepartments(): Array<AuthDepartmentModel> {
        return this._departments ?? [];
    }

    async setAcls() {
        var user = await this.getCurrentUser();
        this._acls = [];
        if (user && user.roles && user.roles.length > 0) {
            for (let role of user.roles) {
                for (let acl of role.accessControlLists) {
                    if (this._acls.indexOf(acl) < 0) {
                        this._acls.push(acl);
                    }
                }
            }
        }
        this._userAclsSubject.next(this._acls);
    }

    getAcls(): Array<string> {
        return this._acls;
    }

    async isAuthorized(acl: string): Promise<boolean> {
        var user = await this.getCurrentUser();
        if (user?.isAdminUser) {
            if (environment.isLocal) {
                console.info(`User: ${user.email} is admin`);
            }
            return true;
        }

        if (!this._acls || this._acls.length === 0) {
            await this.setAcls();
        }

        var hasAccess =
            this._acls.findIndex((c) => c.toLowerCase() == acl.toLowerCase()) >=
            0;
        if (environment.isLocal) {
            console.info(
                `Does user: ${user!.email} have access to ${acl}: ${
                    hasAccess ? 'Yes' : 'No'
                }`
            );
        }
        return hasAccess;
    }

    async checkUserLastToken(lastToken?: string): Promise<void> {
        const storedToken = localStorage
            .getItem(this._userLastSession)
            ?.toUpperCase();

        if (!lastToken || !storedToken) {
            lastToken = crypto.randomUUID().toUpperCase();
            localStorage.setItem(this._userLastSession, lastToken);
            await this._userService
                .putUserLastToken({
                    lastToken: lastToken,
                } as PutUserLastTokenModel)
                .toPromise();
        } else {
            if (environment.isLocal) {
                // console.log(
                //     'Last token found for user, validating with server.'
                // );
            }
            lastToken = lastToken?.toUpperCase();
            if (lastToken == storedToken) {
                if (environment.isLocal) {
                    //console.log('User last token matched, all good.');
                }
                return;
            }

            await this.logout(false,true);
        }
    }

    async logout(clearSession:boolean, clearLastToken:boolean=false): Promise<void> {
        if(clearLastToken){
            localStorage.removeItem(this._userLastSession);
        }
        if (this._currentUser && clearSession) {
            try{
                await this._userService
                .clearSession(this._currentUser.email)
                .toPromise();
            }catch(err){
                console.error('Error clearing user session on server', err);
            }
        }
        try{

            await this._oidcSecurityService
                .logoffAndRevokeTokens(environment.auth.configId, {
                    customParams: {
                        client_id: environment.auth.clientId,
                        logout_uri: window.location.origin,
                    }
                }).toPromise();
            if(clearSession){
                localStorage.removeItem('azureAd');
            }

        }catch(err){
            console.error('Error during logout', err);
        }
    }
}
